import { z } from 'zod'
import { oc } from '@orpc/contract'
import { PATIENT_TYPE_VALUES } from './zocdoc-availability.contract'

/**
 * Book one of a provider's published Zocdoc slots for a beHuman patient — `POST /v1/appointments`
 * (Zocdoc OpenAPI v1.177).
 *
 * **Zocdoc-side only.** Nothing is written to FHIR here. A booking lands in Zocdoc's
 * `pending_booking` state and announces itself over the webhook, and the existing inbound ingest
 * path is what creates the `Appointment`/`Encounter` and the `zocdoc_appointment_link`
 * row. One writer for the FHIR side means a booking made here and one made on the Zocdoc
 * marketplace travel the identical path, and no dedupe is needed between them.
 *
 * Three properties of the upstream call shape this contract:
 *
 * - **it is not idempotent and Zocdoc offers no idempotency key**, so it is never retried on a
 *   429/5xx/timeout (see `createAppointment` in the client) and `dryRun` defaults to **true** —
 *   a real appointment against a real provider is not something a stray click should create;
 * - **`start_time` must carry a UTC offset** and match a start Zocdoc considers bookable for the
 *   chosen visit reason and patient type; the slot we read carries a bare local wall clock, so the
 *   offset is derived from the location's IANA zone;
 * - **the documented 200 carries no appointment id** even though the endpoint's prose promises one,
 *   so `appointmentId` is optional and its absence is reported rather than treated as a failure.
 *
 * No FHIR at the boundary: the patient crosses as an id, the practitioner as an NPI.
 */
export const zocdocCreateAppointment = oc
  .route({ method: 'POST', path: '/v2/zocdoc/appointments' })
  .input(
    z.object({
      /** The practitioner's NPI, resolved to Zocdoc's `pr_…` inside the handler. */
      npi: z.string().min(1),
      /**
       * beHuman `Patient.id` — the patient is read inside the handler, so no FHIR crosses the
       * boundary.
       *
       * Exactly one of `patientId` / `newPatient` must be given. Two sources rather than one
       * because Zocdoc will book for a person we hold no chart for, and requiring a chart first
       * would mean creating one in beHuman before we know the booking is even accepted.
       */
      patientId: z.string().min(1).optional(),
      /**
       * Demographics for someone with no beHuman chart, typed straight into the booking form.
       *
       * The field set is exactly what `POST /v1/appointments` documents as **required** for
       * `data.patient` — nothing optional is invented here, and the same validation an existing
       * chart goes through (phone normalisation, sex-at-birth refusal, state/ZIP shape) applies
       * unchanged. Nothing is written to beHuman: this books on Zocdoc only, and the chart is
       * created by the inbound ingest path like any other Zocdoc booking.
       */
      newPatient: z
        .object({
          firstName: z.string().min(1),
          lastName: z.string().min(1),
          /** `YYYY-MM-DD`. */
          dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
          /** Zocdoc accepts only these two; there is no `other`/`unknown`. */
          sexAtBirth: z.enum(['male', 'female']),
          /** Any human format — normalised to Zocdoc's ten digits in the handler. */
          phone: z.string().min(1),
          email: z.string().min(1),
          address1: z.string().min(1),
          address2: z.string().optional(),
          city: z.string().min(1),
          /** Two-letter code. */
          state: z.string().min(1),
          /** Five digits. */
          zipCode: z.string().min(1),
        })
        .optional(),
      /** The slot's bare `lo_…`, recombined with the provider id into `pr_…|lo_…`. */
      locationId: z.string().min(1),
      /** The slot's local date and wall-clock start, exactly as the slots read returned them. */
      day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
      /**
       * IANA zone the wall clock is in. Omitted = the zone Zocdoc reports for the slot's location,
       * which is the only value Zocdoc itself vouches for.
       */
      timeZone: z.string().optional(),
      /** Zocdoc's `pc_…`. Must be one the slot accepts, or Zocdoc rejects the booking. */
      visitReasonId: z.string().min(1),
      patientType: z.enum(PATIENT_TYPE_VALUES),
      /** Zocdoc caps these at 100 characters. */
      notes: z.string().optional(),
      /**
       * Build and validate without sending anything. The default, because the call creates a real
       * appointment and cannot be replayed safely — booking must be asked for explicitly.
       */
      dryRun: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      dryRun: z.boolean(),
      /** False whenever the booking was not created — on a dry run, a refusal, or a failure. */
      booked: z.boolean(),
      /**
       * The exact body that was (or would be) sent, so the popup can show it before committing and
       * a support ticket can quote it afterwards. Patient PHI, so it is never logged.
       */
      payload: z
        .object({
          startTime: z.string(),
          providerLocationId: z.string(),
          visitReasonId: z.string(),
          patientType: z.enum(PATIENT_TYPE_VALUES),
          patientName: z.string(),
          notes: z.string().optional(),
          /**
           * The patient block exactly as Zocdoc will receive it — **normalised**, which is the
           * point: the phone has been stripped to ten digits and the state upper-cased, so this
           * shows what is actually sent rather than what the chart happens to hold. An operator
           * confirming a booking should be reading the wire values, not a hopeful rendering of
           * them.
           */
          patient: z.object({
            firstName: z.string(),
            lastName: z.string(),
            dateOfBirth: z.string(),
            sexAtBirth: z.enum(['male', 'female']),
            phoneNumber: z.string(),
            emailAddress: z.string(),
            address1: z.string(),
            address2: z.string().optional(),
            city: z.string(),
            state: z.string(),
            zipCode: z.string(),
            /** beHuman's `Patient.id`, sent as Zocdoc's `developer_patient_id`. */
            developerPatientId: z.string().optional(),
            /**
             * Zocdoc's own `patient_id` off the chart, sent as `patient_id` — present exactly when
             * this books as a patient Zocdoc already knows rather than creating a new record.
             *
             * Shown in the preview because those two outcomes are otherwise indistinguishable to
             * the operator confirming the booking, and they differ in what Zocdoc ends up holding.
             */
            zocdocPatientId: z.string().optional(),
          }),
        })
        .optional(),
      /**
       * Everything that makes this patient or slot unbookable, all of it at once — a missing
       * address, a gender Zocdoc has no field for, an unusable phone. Non-empty means nothing was
       * sent, dry run or not.
       */
      problems: z.array(z.string()),
      /**
       * Zocdoc's appointment id **when it sends one**. Absent does not mean the booking failed —
       * check `booked`.
       */
      appointmentId: z.string().optional(),
      /** `in_person` / `zocdoc_video_service` / `third_party_video_service`. */
      visitType: z.string().optional(),
      /** Quote this to Zocdoc support; it identifies the call on their side. */
      requestId: z.string().optional(),
      /**
       * Set when the call itself failed. A transport failure here is the ambiguous case: Zocdoc may
       * have accepted the booking and only the response was lost, so this says so rather than
       * inviting a retry.
       */
      error: z.string().optional(),
      missingEnv: z.array(z.string()).optional(),
    }),
  )
